import { useCallback, useEffect, useRef } from 'react';
import { EQ_FREQUENCIES } from '@/lib/dsp';

type Props = {
  gains: number[];
  onChangeGain?: (index: number, db: number) => void;
};

const MIN_DB = -12;
const MAX_DB = 12;

/** Solve G·w = y so the Gaussian stroke passes through every control dot. */
function solveGaussianWeights(
  xs: number[],
  ys: number[],
  zeroY: number,
  curve: number,
): number[] {
  const n = xs.length;
  const twoCurveSq = 2 * curve * curve;
  const M = Array.from({ length: n }, (_, i) => {
    const row = Array.from({ length: n + 1 }, () => 0);
    for (let j = 0; j < n; j++) {
      const dx = xs[i]! - xs[j]!;
      row[j] = Math.exp((-dx * dx) / twoCurveSq);
    }
    row[i]! += 1e-6;
    row[n] = ys[i]! - zeroY;
    return row;
  });

  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r]![col]!) > Math.abs(M[pivot]![col]!)) pivot = r;
    }
    if (pivot !== col) {
      const tmp = M[col]!;
      M[col] = M[pivot]!;
      M[pivot] = tmp;
    }
    const div = M[col]![col]!;
    if (Math.abs(div) < 1e-12) continue;
    for (let c = col; c <= n; c++) M[col]![c]! /= div;
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = M[r]![col]!;
      for (let c = col; c <= n; c++) M[r]![c]! -= f * M[col]![c]!;
    }
  }
  return M.map((row) => row[n]!);
}

function sampleCurveY(
  x: number,
  xs: number[],
  weights: number[],
  zeroY: number,
  curve: number,
) {
  const twoCurveSq = 2 * curve * curve;
  let y = zeroY;
  for (let i = 0; i < xs.length; i++) {
    const dx = x - xs[i]!;
    y += weights[i]! * Math.exp((-dx * dx) / twoCurveSq);
  }
  return y;
}

function clampDb(db: number) {
  return Math.max(MIN_DB, Math.min(MAX_DB, db));
}

function snapDb(db: number) {
  return Math.round(clampDb(db) * 2) / 2;
}

export function EqGraph({ gains, onChangeGain }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const displayRef = useRef<number[]>([...gains]);
  const targetRef = useRef<number[]>([...gains]);
  const rafRef = useRef(0);
  const dragIndexRef = useRef(-1);
  const drawRef = useRef<(vals: number[]) => void>(() => {});
  const layoutRef = useRef({
    padX: 14,
    padY: 12,
    plotW: 300,
    plotH: 96,
  });

  useEffect(() => {
    if (dragIndexRef.current >= 0) return;
    targetRef.current = [...gains];
    cancelAnimationFrame(rafRef.current);
    const tick = () => {
      const target = targetRef.current;
      const cur = displayRef.current;
      let done = true;
      for (let i = 0; i < cur.length; i++) {
        const t = target[i] ?? 0;
        const c = cur[i] ?? 0;
        const next = c + (t - c) * 0.22;
        if (Math.abs(t - next) > 0.015) done = false;
        cur[i] = next;
      }
      drawRef.current(cur);
      if (!done) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [gains]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cssW = canvas.clientWidth || 320;
      const cssH = canvas.clientHeight || 120;
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // No side pad for band X — centers match `.eq-faders` equal columns.
      const padX = 0;
      const padY = 12;
      const plotW = cssW;
      const plotH = cssH - padY * 2;
      layoutRef.current = { padX, padY, plotW, plotH };

      const yAt = (db: number) =>
        padY + ((MAX_DB - db) / (MAX_DB - MIN_DB)) * plotH;
      const xAt = (i: number, n: number) =>
        n <= 0 ? cssW / 2 : ((i + 0.5) / n) * cssW;

      drawRef.current = (vals: number[]) => {
        const w = cssW;
        const h = cssH;
        const n = EQ_FREQUENCIES.length;
        ctx.clearRect(0, 0, w, h);

        const fg =
          getComputedStyle(canvas).getPropertyValue('--eq-stroke').trim() ||
          'hsl(40 3% 18%)';
        const grid =
          getComputedStyle(canvas).getPropertyValue('--eq-grid').trim() ||
          'hsla(40, 3%, 18%, 0.12)';
        const fill =
          getComputedStyle(canvas).getPropertyValue('--eq-fill').trim() ||
          'hsla(40, 3%, 18%, 0.1)';

        const zeroY = yAt(0);

        // Minimal graph lines
        ctx.strokeStyle = grid;
        ctx.lineWidth = 1;
        for (const db of [12, 6, -6, -12] as const) {
          const yy = yAt(db);
          ctx.globalAlpha = Math.abs(db) === 12 ? 0.32 : 0.18;
          ctx.beginPath();
          ctx.moveTo(padX, yy);
          ctx.lineTo(padX + plotW, yy);
          ctx.stroke();
        }
        ctx.globalAlpha = 0.5;
        ctx.beginPath();
        ctx.moveTo(padX, zeroY);
        ctx.lineTo(padX + plotW, zeroY);
        ctx.stroke();

        ctx.globalAlpha = 0.12;
        for (let i = 0; i < n; i++) {
          const xx = xAt(i, n);
          ctx.beginPath();
          ctx.moveTo(xx, padY);
          ctx.lineTo(xx, padY + plotH);
          ctx.stroke();
        }
        ctx.globalAlpha = 1;

        ctx.fillStyle = grid;
        ctx.font = '9px var(--font-sans, sans-serif)';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.globalAlpha = 0.45;
        ctx.fillText('+12', 1, yAt(12));
        ctx.fillText('0', 4, zeroY);
        ctx.fillText('−12', 1, yAt(-12));
        ctx.globalAlpha = 1;

        const xs = vals.map((_, i) => xAt(i, n));
        const ys = vals.map((g) => yAt(g ?? 0));
        const spacing = n > 1 ? (xs[n - 1]! - xs[0]!) / (n - 1) : 40;
        const curve = Math.max(spacing * 0.78, 16);
        const weights = solveGaussianWeights(xs, ys, zeroY, curve);
        const x0 = Math.floor(padX);
        const x1 = Math.ceil(padX + plotW);

        ctx.beginPath();
        for (let x = x0; x <= x1; x += 1) {
          const y = sampleCurveY(x, xs, weights, zeroY, curve);
          if (x === x0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = fg;
        ctx.lineWidth = 2;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.stroke();

        ctx.beginPath();
        for (let x = x0; x <= x1; x += 1) {
          const y = sampleCurveY(x, xs, weights, zeroY, curve);
          if (x === x0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.lineTo(x1, zeroY);
        ctx.lineTo(x0, zeroY);
        ctx.closePath();
        ctx.fillStyle = fill;
        ctx.fill();

        const dragIdx = dragIndexRef.current;
        for (let i = 0; i < n; i++) {
          const r = i === dragIdx ? 5 : 3.5;
          ctx.beginPath();
          ctx.arc(xs[i]!, ys[i]!, r, 0, Math.PI * 2);
          ctx.fillStyle = fg;
          ctx.fill();
          if (i === dragIdx) {
            ctx.strokeStyle = fg;
            ctx.lineWidth = 1.5;
            ctx.globalAlpha = 0.35;
            ctx.beginPath();
            ctx.arc(xs[i]!, ys[i]!, r + 3.5, 0, Math.PI * 2);
            ctx.stroke();
            ctx.globalAlpha = 1;
          }
        }
      };

      drawRef.current(displayRef.current);
    };

    setup();
    const ro = new ResizeObserver(setup);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  const hitIndex = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return -1;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const { padX, padY, plotW, plotH } = layoutRef.current;
    const n = EQ_FREQUENCIES.length;
    const yAt = (db: number) =>
      padY + ((MAX_DB - db) / (MAX_DB - MIN_DB)) * plotH;
    const xAt = (i: number) =>
      n <= 0 ? plotW / 2 : ((i + 0.5) / n) * plotW;

    let best = -1;
    let bestDist = 24;
    for (let i = 0; i < n; i++) {
      const xi = xAt(i);
      const yi = yAt(targetRef.current[i] ?? 0);
      const d = Math.hypot(x - xi, y - yi);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    if (best >= 0) return best;

    // Column grab near a band
    for (let i = 0; i < n; i++) {
      const xi = xAt(i);
      if (Math.abs(x - xi) < plotW / n / 1.5) {
        const yi = yAt(targetRef.current[i] ?? 0);
        if (Math.abs(y - yi) < plotH * 0.6) return i;
      }
    }
    return -1;
  }, []);

  const applyDrag = useCallback(
    (index: number, clientY: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const { padY, plotH } = layoutRef.current;
      const y = clientY - rect.top;
      const t = (y - padY) / plotH;
      const db = snapDb(MAX_DB - t * (MAX_DB - MIN_DB));
      targetRef.current[index] = db;
      displayRef.current[index] = db;
      drawRef.current(displayRef.current);
      onChangeGain?.(index, db);
    },
    [onChangeGain],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !onChangeGain) return;

    const onDown = (e: PointerEvent) => {
      const idx = hitIndex(e.clientX, e.clientY);
      if (idx < 0) return;
      e.preventDefault();
      dragIndexRef.current = idx;
      canvas.setPointerCapture(e.pointerId);
      canvas.classList.add('is-dragging');
      applyDrag(idx, e.clientY);
    };
    const onMove = (e: PointerEvent) => {
      if (dragIndexRef.current < 0) return;
      e.preventDefault();
      applyDrag(dragIndexRef.current, e.clientY);
    };
    const onUp = (e: PointerEvent) => {
      if (dragIndexRef.current < 0) return;
      dragIndexRef.current = -1;
      canvas.classList.remove('is-dragging');
      drawRef.current(displayRef.current);
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
    };
  }, [applyDrag, hitIndex, onChangeGain]);

  return (
    <canvas
      ref={canvasRef}
      className={`eq-graph${onChangeGain ? ' is-interactive' : ''}`}
      aria-hidden
    />
  );
}
